import { FC, useEffect, useState } from "react";
import { useQuery, useMutation } from "@apollo/client";
import { Messages } from "../../../../../../../shared/chat/messages";
import { message } from "antd";
import { ComponentQueryLoader } from "../../../../../../../shared/component-query-loader";
import {
  CasesCredentialVerificationV1CaseDetailsMessagesContainerCredentialVerificationCaseV1Document,
  CasesCredentialVerificationV1CaseDetailsMessagesContainerCredentialVerificationCaseV1UpdateDocument,
  CredentialVerificationCaseV1UpdateInput,
  CasesCredentialVerificationV1CaseDetailsMessagesContainerApplicantUserPaymentInstrumentDocument,
  CasesCredentialVerificationV1CaseDetailsMessagesContainerApplicantUserCybersourcePublicKeyIdDocument,
  PaymentInstrument
} from "../../../../../../../../generated";
import { lookupCountryQuery } from "./case-details-country-query";

interface CaseDetailsMessagesContainerProps {
  caseId: string;
  applicantId: string;
}

interface Message {
  createdAt: string;
  isHiddenFromApplicant: boolean;
  message: string;
  embedding: string | undefined;
  sentBy: string;
  id: string;
}
interface FinanceDetailsTransactions {
  adhocTransactions?: Array<{
    reason?: string | null;
    amount?: number | null;
    createdAt?: any | null;
    status?: string | null;
    transactionReference?: {
      referenceId?: string | null;
    } | null;
  } | null> | null;
}

export const CaseDetailsMessagesContainer: FC<CaseDetailsMessagesContainerProps> = (props) => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [transactions, setTransactions] = useState<FinanceDetailsTransactions | null>();

  const {
    data: queryData,
    loading,
    error,
  } = useQuery(
    CasesCredentialVerificationV1CaseDetailsMessagesContainerCredentialVerificationCaseV1Document,
    {
      variables: {
        credentialVerificationCaseV1Id: props.caseId,
      },
    }
  );

  const { startPolling, stopPolling, data: pollingData } = useQuery(CasesCredentialVerificationV1CaseDetailsMessagesContainerCredentialVerificationCaseV1Document,
    {
      variables: {
        credentialVerificationCaseV1Id: props.caseId,
      },
    }
  );

  const {
    data: paymentInstrumentData,
    loading: paymentInstrumentLoading,
    error: paymentInstrumentError,
  } = useQuery(
    CasesCredentialVerificationV1CaseDetailsMessagesContainerApplicantUserPaymentInstrumentDocument,
    {
      variables: {
        applicantUserId: props.applicantId,
      },
    }
  );

  const {
    data: countryData,
    loading: countryLoading,
    error: countryError,
  } = useQuery(lookupCountryQuery);

  const {
    data: cybersourcePublicKeyData,
    loading: cybersourcePublicKeyLoading,
    error: cybersourcePublicKeyError,
  } = useQuery(CasesCredentialVerificationV1CaseDetailsMessagesContainerApplicantUserCybersourcePublicKeyIdDocument);

  const [updatedMessages] = useMutation(
    CasesCredentialVerificationV1CaseDetailsMessagesContainerCredentialVerificationCaseV1UpdateDocument
  );

  useEffect(() => {
    if(queryData?.credentialVerificationCaseV1){
      setTransactions(queryData?.credentialVerificationCaseV1?.financeDetails?.transactions);
    }
    if (queryData?.credentialVerificationCaseV1?.messages) {
      setMessages(queryData?.credentialVerificationCaseV1?.messages as Message[]);
    }
  }, [queryData]);

  const handleSave = async (values: any) => {

    const inputData: CredentialVerificationCaseV1UpdateInput = {
      id: props.caseId,
      messages: values?.messages,
    };

    if (values?.revisionRequest) {
      inputData.revisionRequest = {
        ...values?.revisionRequest,
      };
    }

    if(values?.financeDetails){
      inputData.financeDetails = {
        ...values?.financeDetails,
      };
    }
    if (values?.adhocPayment) {
      inputData.adhocPayment = {
        ...values?.adhocPayment,
      };
    }
    try {
      const result = await updatedMessages({
        variables: {
          input: inputData,
        },
      });

      if (result.data?.credentialVerificationCaseV1Update?.status.success) {
        if (
          result.data?.credentialVerificationCaseV1Update
            .credentialVerificationCaseV1
        ) {
          setMessages(
            result.data?.credentialVerificationCaseV1Update
              .credentialVerificationCaseV1.messages as Message[] || []
          );
        }
      } else {
        message.error(
          `Error in sending message: ${result.data?.credentialVerificationCaseV1Update?.status.errorMessage}`
        );
      }
    } catch (error) {
      message.error(`Error in sending message: ${JSON.stringify(error)}`);
    }
  };

  const lastName =
  queryData?.credentialVerificationCaseV1?.applicant?.personalInformation
    ?.identityDetails?.lastName ?? "";
const restOfName =
  queryData?.credentialVerificationCaseV1?.applicant?.personalInformation
    ?.identityDetails?.restOfName ?? "";

  return (
    <ComponentQueryLoader
      error={error || countryError || cybersourcePublicKeyError || paymentInstrumentError}
      loading={loading || countryLoading || cybersourcePublicKeyLoading || paymentInstrumentLoading}
      hasData={messages !== undefined}
      hasDataComponent={
        <Messages
          disabled={queryData?.credentialVerificationCaseV1?.state === 'decisionRendered'}
          caseId={props.caseId}
          data={{messages, applicant: { lastName, restOfName }}}
          onSave={handleSave}
          startPollingMessages={startPolling}
          pollingMessagesData={{ data: { id: pollingData?.credentialVerificationCaseV1?.id, messages: pollingData?.credentialVerificationCaseV1?.messages as Message[] } }}
          stopPollingMessages={stopPolling}
          updateMessage={() => {}}
          transactions={transactions}
          paymentInstrument={paymentInstrumentData?.applicantUserPaymentInstrument as PaymentInstrument}
          applicantId={props.applicantId}
          country={countryData?.lookupCountry}
          cyberSourcePublicKey= {cybersourcePublicKeyData?.applicantUserCybersourcePublicKeyId as string}
        />
      }
    />
  );
};
